import { logger } from '@/core/logger';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

/**
 * Secure storage wrapper for expo-secure-store
 * Provides consistent API across platforms with proper error handling
 */
export class SecureStorage {
    /**
     * Store a value securely
     * @param key - Storage key
     * @param value - Value to store (will be converted to string)
     * @returns Promise<void>
     */
    static async setItem(key: string, value: string | null): Promise<void> {
        try {
            if (typeof value === 'string' && value.length > 1800) {
                logger.warn('SecureStorage:Value approaching size limit', { key, size: value.length });
            }
            if (Platform.OS === 'web') {
                // Web fallback to localStorage
                if (value === null) {
                    localStorage.removeItem(key);
                } else {
                    localStorage.setItem(key, value);
                }
                logger.debug('SecureStorage:Item stored in localStorage', { key });
            } else {
                // Native secure storage
                if (value == null) {
                    await SecureStore.deleteItemAsync(key);
                } else {
                    await SecureStore.setItemAsync(key, value);
                }
                logger.debug('SecureStorage:Item stored securely', { key });
            }
        } catch (error) {
            logger.error('SecureStorage:Failed to store item', { key, error });
            throw new Error(`Failed to store item securely: ${key}`);
        }
    }

    /**
     * Retrieve a value from secure storage
     * @param key - Storage key
     * @returns Promise<string | null>
     */
    static async getItem(key: string): Promise<string | null> {
        try {
            if (Platform.OS === 'web') {
                // Web fallback to localStorage
                if (typeof localStorage !== 'undefined') {
                    const value = localStorage.getItem(key);
                    logger.debug('SecureStorage:Item retrieved from localStorage', { key, hasValue: !!value });
                    return value;
                }
                return null;
            } else {
                // Native secure storage
                const value = await SecureStore.getItemAsync(key);
                logger.debug('SecureStorage:Item retrieved securely', { key, hasValue: !!value });
                return value;
            }
        } catch (error) {
            logger.error('SecureStorage:Failed to retrieve item', { key, error });
            return null;
        }
    }

    /**
     * Remove a value from secure storage
     * @param key - Storage key
     * @returns Promise<void>
     */
    static async removeItem(key: string): Promise<void> {
        try {
            if (Platform.OS === 'web') {
                localStorage.removeItem(key);
                logger.debug('SecureStorage:Item removed from localStorage', { key });
            } else {
                await SecureStore.deleteItemAsync(key);
                logger.debug('SecureStorage:Item removed securely', { key });
            }
        } catch (error) {
            logger.error('SecureStorage:Failed to remove item', { key, error });
            throw new Error(`Failed to remove item securely: ${key}`);
        }
    }

    /**
     * Check if secure storage is available
     * @returns boolean
     */
    static isAvailable(): boolean {
        if (Platform.OS === 'web') {
            return typeof localStorage !== 'undefined';
        } else {
            // For native, we assume it's available (expo-secure-store handles this internally)
            return true;
        }
    }

    /**
     * Store multiple items atomically
     * @param items - Array of [key, value] pairs
     * @returns Promise<void>
     */
    static async setMultiple(items: Array<[string, string | null]>): Promise<void> {
        try {
            await Promise.all(
                items.map(([key, value]) => this.setItem(key, value))
            );
            logger.debug('SecureStorage:Multiple items stored', { count: items.length });
        } catch (error) {
            logger.error('SecureStorage:Failed to store multiple items', { error });
            throw new Error('Failed to store multiple items securely');
        }
    }

    /**
     * Retrieve multiple items
     * @param keys - Array of keys to retrieve
     * @returns Promise<Record<string, string | null>>
     */
    static async getMultiple(keys: string[]): Promise<Record<string, string | null>> {
        try {
            const results = await Promise.all(
                keys.map(async (key) => [key, await this.getItem(key)] as [string, string | null])
            );

            const result = Object.fromEntries(results);
            logger.debug('SecureStorage:Multiple items retrieved', { count: keys.length });
            return result;
        } catch (error) {
            logger.error('SecureStorage:Failed to retrieve multiple items', { error });
            return {};
        }
    }

    /**
     * Remove multiple items
     * @param keys - Array of keys to remove
     * @returns Promise<void>
     */
    static async removeMultiple(keys: string[]): Promise<void> {
        try {
            await Promise.all(
                keys.map((key) => this.removeItem(key))
            );
            logger.debug('SecureStorage:Multiple items removed', { count: keys.length });
        } catch (error) {
            logger.error('SecureStorage:Failed to remove multiple items', { error });
            throw new Error('Failed to remove multiple items securely');
        }
    }
}

// Export default instance for convenience
export const secureStorage = SecureStorage;
